import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import { EditRequestDiff } from "@/components/editRequests/EditRequestDiff";
import { useBusiness } from "@/hooks/useBusinesses";
import {
  type EditRequestSummary,
  useApproveEditRequest,
  useRejectEditRequest,
} from "@/hooks/useBusinessEditRequests";

interface Props {
  request: EditRequestSummary | null;
  onClose: () => void;
  onReviewed: () => void;
}

export const EditRequestDetailDialog = ({
  request,
  onClose,
  onReviewed,
}: Props) => {
  const [mode, setMode] = useState<"view" | "reject">("view");
  const [rejectionReason, setRejectionReason] = useState("");
  const [note, setNote] = useState("");

  const { data: business } = useBusiness(request?.businessId, {
    enabled: !!request,
  });
  const approve = useApproveEditRequest();
  const reject = useRejectEditRequest();

  const reset = () => {
    setMode("view");
    setRejectionReason("");
    setNote("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleApprove = async () => {
    if (!request) return;
    try {
      await approve.mutateAsync({
        id: request.id,
        businessId: request.businessId,
        note: note.trim() || undefined,
      });
      toast.success("Edit request approved — changes applied.");
      reset();
      onReviewed();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleReject = async () => {
    if (!request) return;
    if (rejectionReason.trim().length < 3) {
      toast.error("Please provide a rejection reason.");
      return;
    }
    try {
      await reject.mutateAsync({
        id: request.id,
        businessId: request.businessId,
        rejectionReason: rejectionReason.trim(),
        note: note.trim() || undefined,
      });
      toast.success("Edit request rejected.");
      reset();
      onReviewed();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const proposed = (request?.changes ?? {}) as Record<string, unknown>;
  const current = (business ?? {}) as unknown as Record<string, unknown>;
  const canAct = request?.status === "PENDING";

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Edit request — {request?.business.businessName}
          </DialogTitle>
          <DialogDescription>
            Review the changes requested by the provider. Approving applies them
            to the public business profile immediately.
          </DialogDescription>
        </DialogHeader>

        {request ? (
          <div className="space-y-4">
            {request.status !== "PENDING" ? (
              <div className="rounded-card border border-border bg-muted/30 p-3 text-sm">
                <div className="font-medium capitalize">
                  {request.status.toLowerCase()}
                </div>
                {request.rejectionReason ? (
                  <div className="text-muted-foreground mt-1">
                    <span className="font-medium">Reason:</span>{" "}
                    {request.rejectionReason}
                  </div>
                ) : null}
                {request.note ? (
                  <div className="text-muted-foreground mt-1">
                    <span className="font-medium">Note:</span> {request.note}
                  </div>
                ) : null}
                {request.reviewer ? (
                  <div className="text-muted-foreground mt-1">
                    Reviewed by {request.reviewer.name}
                  </div>
                ) : null}
              </div>
            ) : null}

            <EditRequestDiff current={current} proposed={proposed} />

            {canAct && mode === "reject" ? (
              <div className="space-y-2">
                <Label htmlFor="rej-reason">Rejection reason</Label>
                <Textarea
                  id="rej-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Tell the provider what to correct…"
                  rows={3}
                />
                <Label htmlFor="rej-note">Additional note (optional)</Label>
                <Textarea
                  id="rej-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
          {canAct ? (
            mode === "reject" ? (
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={reject.isPending}
              >
                Confirm reject
              </Button>
            ) : (
              <>
                <PermissionGate module="businesses" action="reject">
                  <Button
                    variant="destructive"
                    onClick={() => setMode("reject")}
                  >
                    Reject
                  </Button>
                </PermissionGate>
                <PermissionGate module="businesses" action="approve">
                  <Button onClick={handleApprove} disabled={approve.isPending}>
                    Approve & apply
                  </Button>
                </PermissionGate>
              </>
            )
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
